import type { ReactNode } from 'react';
import type { Size2MixSecondaryNavItem } from './types';

export interface Size2MixSecondaryNavProps<Id extends string> {
    /** Content shown as the first (non-clickable) item — network name, page title… */
    header?: ReactNode;
    items: Size2MixSecondaryNavItem<Id>[];
    activeId: Id;
    onSelect?: (id: Id) => void;
    width?: number;
}

/**
 * Secondary navigation column (256px) — same DOM as Nemo's network menu:
 * `ul.nj-list-deprecated` with 72px items, blue-alpha background + left border on the active item.
 */
export function Size2MixSecondaryNav<Id extends string>({ header, items, activeId, onSelect, width = 256 }: Size2MixSecondaryNavProps<Id>) {
    return (
        <nav aria-label="Navigation secondaire" className="nemo-secondary-nav" style={{ width, minWidth: width, height: '100%', overflowY: 'auto', flexShrink: 0 }}>
            <ul className="nj-list-deprecated">
                {header && (
                    <li className="nj-list-deprecated__item" style={{ border: 'none' }}>
                        <span className="nj-list-deprecated__item-content">{header}</span>
                    </li>
                )}
                {items.map((item) => {
                    const isActive = item.id === activeId;
                    return (
                        <li
                            key={item.id}
                            className={`nj-list-deprecated__item nj-list-deprecated__item--clickable${isActive ? ' nj-list-deprecated__item--active' : ''}${item.isDisabled ? ' nj-list-deprecated__item--disabled' : ''}`}
                            style={{ border: 'none' }}
                        >
                            <button type="button" disabled={item.isDisabled} aria-current={isActive ? 'page' : undefined} onClick={() => onSelect?.(item.id)}>
                                <span className="material-icons nj-icon-material nj-icon-material--md nj-list-deprecated__item-icon" aria-hidden="true">
                                    {item.icon}
                                </span>
                                <span className="nj-list-deprecated__item-content">{item.label}</span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}

export interface Size2MixSecondaryNavTitleProps {
    icon?: ReactNode;
    title: string;
}

/** Title item of the secondary nav (fluid icon + network name, 14px bold, break-all). */
export function Size2MixSecondaryNavTitle({ icon, title }: Size2MixSecondaryNavTitleProps) {
    return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {icon && <span style={{ display: 'flex', width: 24, flexShrink: 0, justifyContent: 'center', paddingRight: 4 }}>{icon}</span>}
            <span style={{ flex: 1, fontSize: 14, lineHeight: '20px', fontWeight: 700, wordBreak: 'break-all' }}>{title}</span>
        </span>
    );
}
